// src/components/Auth/LoginPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../../firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useAuth } from '../../context/AuthContext';

// Mantine Imports
import {
  Box,
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Alert,
  Stack,
  useMantineTheme,
  useMantineColorScheme,
  Center // For the redirecting message
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';

// Background Image Context
import { useBackgroundImage } from '../../context/BackgroundImageContext';
import backgroundImageFile from '../../assets/images/brochure-background.jpg';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  // Background image and theme states
  const { isBackgroundImageVisible } = useBackgroundImage();
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  // Redirect if user is already logged in (no changes to this logic)
  useEffect(() => {
    if (currentUser) {
      navigate(location.state?.from?.pathname || '/menu', { replace: true }); // Default to /menu
    }
  }, [currentUser, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || !password) {
      setError('Please enter both email and password.');
      setLoading(false);
      return;
    }

    try {
      await signInWithEmailAndPassword(auth, email, password);
      const from = location.state?.from?.pathname || '/menu'; // Default to /menu
      navigate(from, { replace: true });
    } catch (err) {
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('Invalid email or password. Please check your credentials.');
      } else {
        setError('Failed to log in. Please try again.');
      }
      console.error("Login error:", err);
    }
    setLoading(false);
  };

  // Fallback UI for "already logged in" state (briefly shown during redirect)
  if (currentUser && !loading) {
    return (
      <Box
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDark ? theme.colors.dark[7] : theme.colors.gray[0], // Simple bg for this state
        }}
      >
        <Text>Already logged in. Redirecting...</Text>
      </Box>
    );
  }

  return (
    <Box // Root Box for background image and centering
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing.md, // Overall padding for the page
        backgroundImage: isBackgroundImageVisible
          ? `url(${backgroundImageFile})`
          : 'none', // No explicit color, let App.js MantineProvider bg take over or set one
        backgroundColor: isBackgroundImageVisible
          ? undefined // Let image show
          : (isDark ? theme.colors.dark[8] : theme.colors.gray[1]), // Subtle bg if image is off
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed', // Consistent with other pages
      }}
    >
      <Container size="xs" style={{ width: '100%', maxWidth: 420 }}> {/* Max width for login form */}
        <Paper withBorder shadow="xl" p="xl" radius="md" style={{ width: '100%' }}>
          <Stack>
            <Title order={2} ta="center" mb="lg">
              Writer Hub Login
            </Title>

            {error && (
              <Alert
                title="Login Error"
                color="red"
                icon={<IconAlertCircle size="1.1rem" />}
                onClose={() => setError('')}
                withCloseButton
                mb="md"
                radius="md"
              >
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <Stack gap="lg">
                <TextInput
                  label="Email"
                  placeholder="your@email.com"
                  type="email" // Browser validation for email format
                  value={email}
                  onChange={(event) => setEmail(event.currentTarget.value)}
                  required
                  size="md"
                  radius="md"
                />
                <PasswordInput
                  label="Password"
                  placeholder="Your password"
                  value={password}
                  onChange={(event) => setPassword(event.currentTarget.value)}
                  required
                  size="md"
                  radius="md"
                />
                <Button
                  type="submit"
                  loading={loading}
                  fullWidth
                  mt="md" // Margin top before button
                  size="md"
                  radius="md"
                >
                  {loading ? 'Logging in...' : 'Log In'}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export default LoginPage;